import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore'
import { isNoSignInPreviewActive } from '../debug'
import { firestoreDb } from './firebase'
import {
  OVERVIEW_BUILTIN_KEYS,
  OVERVIEW_EMBED_HOST_ALLOWLIST,
  OVERVIEW_GRID_COLUMNS,
  OVERVIEW_LOCK_TTL_MS,
  OVERVIEW_SECTION_LIMITS,
  OVERVIEW_V2_LIMITS,
  OVERVIEW_VERSION_RETENTION,
  TEAM_ONLY_OVERVIEW_SECTIONS,
  type OverviewBlock,
  type OverviewBlockWidth,
  type OverviewBuiltinKey,
  type OverviewCalloutTone,
  type OverviewChartHeight,
  type OverviewCustomSection,
  type OverviewDocument,
  type OverviewDocumentV2,
  type OverviewEditingLock,
  type OverviewEmbedRatio,
  type OverviewImageRatio,
  type OverviewSectionRef,
  type OverviewAnalyticsSource,
  type OverviewMediaAspectRatio,
  type OverviewMode,
  type OverviewPageModeState,
  type OverviewSection,
  type OverviewSectionAnalytics,
  type OverviewSectionBackgroundStyle,
  type OverviewSectionConfig,
  type OverviewSectionData,
  type OverviewSectionDensity,
  type OverviewSectionGroupBy,
  type OverviewSectionKind,
  type OverviewSectionMedia,
  type OverviewSectionSize,
  type OverviewSectionSortDir,
  type OverviewSectionSortKey,
  type OverviewSectionStatusFilter,
  type OverviewSectionVisual,
  type OverviewStyleVariant,
  type OverviewPageBackgroundPattern,
  type OverviewPageFontPair,
  type OverviewPageRadius,
  type OverviewPageTheme,
} from '../types/overview'
import type { WorkspaceItemReference } from '../components/Common/WorkspaceItemPicker'

const versionsCollection = (workspaceId: string) =>
  collection(firestoreDb, 'workspaces', workspaceId, 'overviewVersions')

const workspaceDoc = (workspaceId: string) =>
  doc(firestoreDb, 'workspaces', workspaceId)

const VALID_KINDS: OverviewSectionKind[] = [
  'HERO',
  'PULSE',
  'GOALS',
  'MILESTONES',
  'TASKS',
  'CHECKLISTS',
  'MEMBERS',
  'RESOURCES',
  'FORMS',
  'BUDGET',
  'RISK',
  'ACTIVITY',
  'ANNOUNCEMENT',
  'RICH_TEXT',
  'IMAGE',
  'EMBED',
  'DIVIDER',
  'SPACER',
  'HEATMAP',
  'MEMBER_LOAD',
  'TYPE_MIX',
  'CALENDAR_STRIP',
  'PROGRESS_TREND',
  'BURNDOWN',
  'BUDGET_BREAKDOWN',
  'RISK_MATRIX',
]

const VALID_VARIANTS: OverviewStyleVariant[] = ['PRISM', 'NOIR', 'SIGNAL', 'PAPER']

const clampNumber = (value: unknown, fallback: number, min: number, max: number): number => {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback
  return Math.min(max, Math.max(min, numeric))
}

const cleanString = (value: unknown, fallback = ''): string => {
  if (typeof value !== 'string') return fallback
  return value.trim().slice(0, 600)
}

const cleanLayout = (raw: unknown, fallback: OverviewSectionSize): OverviewSectionSize => {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  const w = clampNumber(value.w, fallback.w, 3, OVERVIEW_GRID_COLUMNS)
  return {
    x: clampNumber(value.x, fallback.x, 0, OVERVIEW_GRID_COLUMNS - 1),
    y: clampNumber(value.y, fallback.y, 0, 80),
    w,
    h: clampNumber(value.h, fallback.h, 2, 8),
  }
}

const cleanLinks = (raw: unknown): Array<{ id: string; label: string; url: string }> => {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item, index) => {
      const value = item && typeof item === 'object' ? item as Record<string, unknown> : {}
      const label = cleanString(value.label)
      const url = cleanString(value.url)
      if (!label || !/^https?:\/\//.test(url)) return null
      return {
        id: cleanString(value.id, `resource-${index}`) || `resource-${index}`,
        label,
        url,
      }
    })
    .filter((item): item is { id: string; label: string; url: string } => Boolean(item))
    .slice(0, 8)
}

const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
const VALID_DENSITIES: OverviewSectionDensity[] = ['cozy', 'compact', 'spacious']
const VALID_BACKGROUND_STYLES: OverviewSectionBackgroundStyle[] = ['variant', 'flat', 'tint', 'outline', 'glass']
const VALID_SORT_KEYS: OverviewSectionSortKey[] = ['priority', 'progress', 'endDate', 'title', 'updatedAt', 'weight']
const VALID_SORT_DIRS: OverviewSectionSortDir[] = ['asc', 'desc']
const VALID_STATUS_FILTERS: OverviewSectionStatusFilter[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'COMPLETE']
const VALID_GROUP_BY: OverviewSectionGroupBy[] = ['none', 'type', 'status', 'assignee']
const VALID_PAGE_RADII: OverviewPageRadius[] = ['sharp', 'soft', 'pill']
const VALID_PAGE_FONTS: OverviewPageFontPair[] = ['system', 'grotesk', 'serif', 'mono-display']
const VALID_PAGE_PATTERNS: OverviewPageBackgroundPattern[] = ['none', 'grid', 'dots', 'noise', 'gradient']

const cleanTheme = (raw: unknown): OverviewPageTheme | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const theme: OverviewPageTheme = {}
  if (typeof value.accentColor === 'string' && HEX_COLOR.test(value.accentColor.trim())) {
    theme.accentColor = value.accentColor.trim()
  }
  if (typeof value.radius === 'string' && VALID_PAGE_RADII.includes(value.radius as OverviewPageRadius)) {
    theme.radius = value.radius as OverviewPageRadius
  }
  if (typeof value.fontPair === 'string' && VALID_PAGE_FONTS.includes(value.fontPair as OverviewPageFontPair)) {
    theme.fontPair = value.fontPair as OverviewPageFontPair
  }
  if (typeof value.backgroundPattern === 'string' && VALID_PAGE_PATTERNS.includes(value.backgroundPattern as OverviewPageBackgroundPattern)) {
    theme.backgroundPattern = value.backgroundPattern as OverviewPageBackgroundPattern
  }
  return Object.keys(theme).length ? theme : undefined
}

const cleanVisual = (raw: unknown): OverviewSectionVisual | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const visual: OverviewSectionVisual = {}
  if (typeof value.accentColor === 'string' && HEX_COLOR.test(value.accentColor.trim())) {
    visual.accentColor = value.accentColor.trim()
  }
  if (typeof value.iconOverride === 'string') {
    const glyph = value.iconOverride.trim().slice(0, 4)
    if (glyph) visual.iconOverride = glyph
  }
  if (typeof value.showHeader === 'boolean') visual.showHeader = value.showHeader
  if (typeof value.density === 'string' && VALID_DENSITIES.includes(value.density as OverviewSectionDensity)) {
    visual.density = value.density as OverviewSectionDensity
  }
  if (typeof value.backgroundStyle === 'string' && VALID_BACKGROUND_STYLES.includes(value.backgroundStyle as OverviewSectionBackgroundStyle)) {
    visual.backgroundStyle = value.backgroundStyle as OverviewSectionBackgroundStyle
  }
  return Object.keys(visual).length ? visual : undefined
}

const cleanData = (raw: unknown): OverviewSectionData | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const data: OverviewSectionData = {}
  if (typeof value.sortKey === 'string' && VALID_SORT_KEYS.includes(value.sortKey as OverviewSectionSortKey)) {
    data.sortKey = value.sortKey as OverviewSectionSortKey
  }
  if (typeof value.sortDir === 'string' && VALID_SORT_DIRS.includes(value.sortDir as OverviewSectionSortDir)) {
    data.sortDir = value.sortDir as OverviewSectionSortDir
  }
  if (Array.isArray(value.statusFilter)) {
    const filters = value.statusFilter
      .filter((entry): entry is string => typeof entry === 'string' && VALID_STATUS_FILTERS.includes(entry as OverviewSectionStatusFilter))
      .map((entry) => entry as OverviewSectionStatusFilter)
    if (filters.length) data.statusFilter = Array.from(new Set(filters))
  }
  if (typeof value.timeframeDays === 'number' && Number.isFinite(value.timeframeDays)) {
    const days = Math.round(value.timeframeDays)
    if (days > 0 && days <= 3650) data.timeframeDays = days
  }
  if (typeof value.groupBy === 'string' && VALID_GROUP_BY.includes(value.groupBy as OverviewSectionGroupBy)) {
    data.groupBy = value.groupBy as OverviewSectionGroupBy
  }
  return Object.keys(data).length ? data : undefined
}

const VALID_ASPECT_RATIOS: OverviewMediaAspectRatio[] = ['16:9', '4:3', '1:1', 'auto']
const VALID_ANALYTICS_SOURCES: OverviewAnalyticsSource[] = ['DASHBOARD', 'TASKS', 'GOALS']

const cleanAnalytics = (raw: unknown): OverviewSectionAnalytics | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const analytics: OverviewSectionAnalytics = {}
  if (typeof value.source === 'string' && VALID_ANALYTICS_SOURCES.includes(value.source as OverviewAnalyticsSource)) {
    analytics.source = value.source as OverviewAnalyticsSource
  }
  if (typeof value.windowDays === 'number' && Number.isFinite(value.windowDays)) {
    const days = Math.round(value.windowDays)
    if (days >= 1 && days <= 730) analytics.windowDays = days
  }
  if (typeof value.windowWeeks === 'number' && Number.isFinite(value.windowWeeks)) {
    const weeks = Math.round(value.windowWeeks)
    if (weeks >= 1 && weeks <= 104) analytics.windowWeeks = weeks
  }
  if (typeof value.targetId === 'string') {
    const target = value.targetId.trim().slice(0, 200)
    if (target) analytics.targetId = target
  }
  return Object.keys(analytics).length ? analytics : undefined
}

export const isHttpsUrl = (raw: string): URL | null => {
  try {
    const url = new URL(raw)
    if (url.protocol !== 'https:') return null
    return url
  } catch {
    return null
  }
}

export const isEmbedAllowed = (url: URL): boolean => {
  const host = url.hostname.toLowerCase()
  return OVERVIEW_EMBED_HOST_ALLOWLIST.some((allowed) => host === allowed || host.endsWith(`.${allowed}`))
}

export const isEmbedAllowedUrl = (raw: string): boolean => {
  const parsed = isHttpsUrl(raw)
  if (!parsed) return false
  return isEmbedAllowed(parsed)
}

const cleanMedia = (raw: unknown): OverviewSectionMedia | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const media: OverviewSectionMedia = {}
  if (typeof value.markdown === 'string') {
    const trimmed = value.markdown.slice(0, 4000)
    if (trimmed) media.markdown = trimmed
  }
  if (typeof value.imageUrl === 'string' && isHttpsUrl(value.imageUrl.trim())) {
    media.imageUrl = value.imageUrl.trim()
  }
  if (typeof value.imageAlt === 'string') {
    const alt = value.imageAlt.trim().slice(0, 200)
    if (alt) media.imageAlt = alt
  }
  if (typeof value.embedUrl === 'string') {
    const url = isHttpsUrl(value.embedUrl.trim())
    if (url && isEmbedAllowed(url)) media.embedUrl = url.toString()
  }
  if (typeof value.aspectRatio === 'string' && VALID_ASPECT_RATIOS.includes(value.aspectRatio as OverviewMediaAspectRatio)) {
    media.aspectRatio = value.aspectRatio as OverviewMediaAspectRatio
  }
  return Object.keys(media).length ? media : undefined
}

const cleanConfig = (raw: unknown): OverviewSectionConfig => {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  return {
    title: cleanString(value.title) || undefined,
    description: cleanString(value.description) || undefined,
    targetIds: Array.isArray(value.targetIds)
      ? value.targetIds.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0).slice(0, 12)
      : undefined,
    resourceLinks: cleanLinks(value.resourceLinks),
    limit: typeof value.limit === 'number' ? clampNumber(value.limit, 4, 1, 12) : undefined,
    visual: cleanVisual(value.visual),
    data: cleanData(value.data),
    media: cleanMedia(value.media),
    analytics: cleanAnalytics(value.analytics),
  }
}

const section = (
  id: string,
  kind: OverviewSectionKind,
  title: string,
  variant: OverviewStyleVariant,
  layout: OverviewSectionSize,
  config: OverviewSectionConfig = {},
): OverviewSection => ({ id, kind, title, variant, layout, config })

// Default TEAM layout is intentionally staggered — tiles don't align to a single
// row grid so the page reads as a designed dashboard rather than a spreadsheet.
// Small gaps between tiles are deliberate and show the grid backdrop.
const defaultTeamSections = (): OverviewSection[] => [
  section('team-hero', 'HERO', 'Command center', 'PRISM', { x: 0, y: 0, w: 8, h: 5 }, {
    description: 'A living team homepage for the mission, active work, blockers, and shared links.',
  }),
  section('team-pulse', 'PULSE', 'Project pulse', 'SIGNAL', { x: 8, y: 1, w: 4, h: 3 }),
  section('team-budget', 'BUDGET', 'Budget signal', 'NOIR', { x: 8, y: 4, w: 4, h: 2 }),
  section('team-goals', 'GOALS', 'Active goals', 'NOIR', { x: 0, y: 5, w: 5, h: 4 }, { limit: 4 }),
  section('team-milestones', 'MILESTONES', 'Milestones', 'PAPER', { x: 5, y: 6, w: 4, h: 3 }, { limit: 4 }),
  section('team-risk', 'RISK', 'Risk radar', 'PRISM', { x: 9, y: 6, w: 3, h: 4 }),
  section('team-checklists', 'CHECKLISTS', 'Checklist runway', 'NOIR', { x: 0, y: 9, w: 5, h: 3 }, { limit: 5 }),
  section('team-tasks', 'TASKS', 'Focus tasks', 'PRISM', { x: 5, y: 9, w: 4, h: 4 }, { limit: 5 }),
  section('team-members', 'MEMBERS', 'Team orbit', 'SIGNAL', { x: 9, y: 10, w: 3, h: 3 }),
  section('team-resources', 'RESOURCES', 'Launch links', 'PAPER', { x: 0, y: 13, w: 6, h: 3 }, {
    resourceLinks: [
      { id: 'roadmap', label: 'Roadmap board', url: 'https://example.com/roadmap' },
      { id: 'handoff', label: 'Team handoff doc', url: 'https://example.com/handoff' },
    ],
  }),
  section('team-activity', 'ACTIVITY', 'Live activity', 'SIGNAL', { x: 6, y: 13, w: 6, h: 3 }),
]

const defaultTeamShelf = (): OverviewSection[] => [
  section('team-forms', 'FORMS', 'Forms stack', 'PAPER', { x: 0, y: 0, w: 4, h: 3 }),
  section('team-note-a', 'ANNOUNCEMENT', 'Daily brief', 'PRISM', { x: 0, y: 0, w: 4, h: 3 }, {
    description: 'Drop the team announcement, operating note, or weekly intent here.',
  }),
  section('team-goals-alt', 'GOALS', 'Goal spotlight', 'SIGNAL', { x: 0, y: 0, w: 4, h: 4 }, { limit: 3 }),
  section('team-checklists-alt', 'CHECKLISTS', 'Proof lane', 'PAPER', { x: 0, y: 0, w: 4, h: 4 }, { limit: 4 }),
  section('team-rich-text', 'RICH_TEXT', 'Rich note', 'PAPER', { x: 0, y: 0, w: 6, h: 3 }),
  section('team-image', 'IMAGE', 'Cover image', 'PRISM', { x: 0, y: 0, w: 6, h: 4 }),
  section('team-embed', 'EMBED', 'Embed', 'NOIR', { x: 0, y: 0, w: 6, h: 5 }),
  section('team-divider', 'DIVIDER', 'Section break', 'PAPER', { x: 0, y: 0, w: 12, h: 2 }),
  section('team-spacer', 'SPACER', 'Spacer', 'PAPER', { x: 0, y: 0, w: 3, h: 2 }),
  section('team-heatmap', 'HEATMAP', 'Activity heatmap', 'SIGNAL', { x: 0, y: 0, w: 8, h: 4 }, { analytics: { windowWeeks: 26 } }),
  section('team-member-load', 'MEMBER_LOAD', 'Member load', 'NOIR', { x: 0, y: 0, w: 6, h: 5 }),
  section('team-type-mix', 'TYPE_MIX', 'Type mix', 'PRISM', { x: 0, y: 0, w: 4, h: 5 }),
  section('team-calendar', 'CALENDAR_STRIP', 'Next 14 days', 'SIGNAL', { x: 0, y: 0, w: 12, h: 3 }, { analytics: { windowDays: 14 } }),
  section('team-trend', 'PROGRESS_TREND', 'Progress trend', 'PAPER', { x: 0, y: 0, w: 6, h: 4 }, { analytics: { windowWeeks: 12 } }),
  section('team-burndown', 'BURNDOWN', 'Burndown', 'PRISM', { x: 0, y: 0, w: 6, h: 4 }),
  section('team-budget-breakdown', 'BUDGET_BREAKDOWN', 'Budget breakdown', 'NOIR', { x: 0, y: 0, w: 6, h: 5 }),
  section('team-risk-matrix', 'RISK_MATRIX', 'Risk matrix', 'SIGNAL', { x: 0, y: 0, w: 5, h: 5 }),
]

const defaultPublicSections = (): OverviewSection[] => [
  section('public-hero', 'HERO', 'Project front page', 'PRISM', { x: 0, y: 0, w: 7, h: 5 }, {
    description: 'A polished outward-facing snapshot of what this workspace is building.',
  }),
  section('public-pulse', 'PULSE', 'Completion signal', 'SIGNAL', { x: 7, y: 1, w: 5, h: 3 }),
  section('public-milestones', 'MILESTONES', 'Roadmap moments', 'NOIR', { x: 7, y: 4, w: 5, h: 4 }, { limit: 4 }),
  section('public-goals', 'GOALS', 'Featured goals', 'PAPER', { x: 0, y: 5, w: 4, h: 4 }, { limit: 3 }),
  section('public-resources', 'RESOURCES', 'Open resources', 'PRISM', { x: 4, y: 6, w: 3, h: 3 }, {
    resourceLinks: [
      { id: 'brief', label: 'Project brief', url: 'https://example.com/brief' },
      { id: 'updates', label: 'Updates hub', url: 'https://example.com/updates' },
    ],
  }),
  section('public-members', 'MEMBERS', 'Team', 'SIGNAL', { x: 0, y: 9, w: 4, h: 3 }),
  section('public-note', 'ANNOUNCEMENT', 'Welcome note', 'PAPER', { x: 4, y: 10, w: 8, h: 3 }, {
    description: 'Use this space for a concise public welcome, context, or launch note.',
  }),
]

const defaultPublicShelf = (): OverviewSection[] => [
  section('public-tasks', 'TASKS', 'Work samples', 'NOIR', { x: 0, y: 0, w: 4, h: 4 }, { limit: 4 }),
  section('public-checklists', 'CHECKLISTS', 'Quality checklist', 'SIGNAL', { x: 0, y: 0, w: 4, h: 4 }, { limit: 4 }),
  section('public-note-alt', 'ANNOUNCEMENT', 'Launch memo', 'PRISM', { x: 0, y: 0, w: 4, h: 3 }),
  section('public-resources-alt', 'RESOURCES', 'Reference shelf', 'PAPER', { x: 0, y: 0, w: 4, h: 4 }),
  section('public-rich-text', 'RICH_TEXT', 'Story block', 'PAPER', { x: 0, y: 0, w: 6, h: 3 }),
  section('public-image', 'IMAGE', 'Hero visual', 'PRISM', { x: 0, y: 0, w: 8, h: 5 }),
  section('public-embed', 'EMBED', 'Demo video', 'NOIR', { x: 0, y: 0, w: 8, h: 5 }),
  section('public-divider', 'DIVIDER', 'Section break', 'PAPER', { x: 0, y: 0, w: 12, h: 2 }),
  section('public-heatmap', 'HEATMAP', 'Activity heatmap', 'SIGNAL', { x: 0, y: 0, w: 8, h: 4 }, { analytics: { windowWeeks: 26 } }),
  section('public-type-mix', 'TYPE_MIX', 'What we build', 'PRISM', { x: 0, y: 0, w: 4, h: 5 }),
  section('public-calendar', 'CALENDAR_STRIP', 'Upcoming schedule', 'SIGNAL', { x: 0, y: 0, w: 12, h: 3 }, { analytics: { windowDays: 21 } }),
  section('public-trend', 'PROGRESS_TREND', 'Progress trend', 'PAPER', { x: 0, y: 0, w: 6, h: 4 }, { analytics: { windowWeeks: 12 } }),
]

export const createDefaultOverviewDocument = (
  workspaceId: string,
  actor: { userId?: string; userName?: string } = {},
): OverviewDocument => ({
  id: `default-${workspaceId}`,
  modes: {
    TEAM: { sections: defaultTeamSections(), shelf: defaultTeamShelf() },
    PUBLIC: { sections: defaultPublicSections(), shelf: defaultPublicShelf() },
  },
  createdBy: actor.userId ?? '',
  createdByName: actor.userName ?? 'Timelining',
  createdAt: null,
})

const fallbackSection = (index: number): OverviewSection =>
  section(`fallback-${index}`, 'ANNOUNCEMENT', 'Overview card', 'PRISM', { x: 0, y: index * 3, w: 4, h: 3 })

const sanitizeSection = (raw: unknown, index: number, mode: OverviewMode): OverviewSection | null => {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  const kind = VALID_KINDS.includes(value.kind as OverviewSectionKind) ? value.kind as OverviewSectionKind : null
  if (!kind) return null
  if (mode === 'PUBLIC' && TEAM_ONLY_OVERVIEW_SECTIONS.includes(kind)) return null
  const fallback = fallbackSection(index)
  const variant = VALID_VARIANTS.includes(value.variant as OverviewStyleVariant)
    ? value.variant as OverviewStyleVariant
    : fallback.variant
  return {
    id: cleanString(value.id, `${mode.toLowerCase()}-${kind.toLowerCase()}-${index}`),
    kind,
    title: cleanString(value.title, fallback.title),
    variant,
    layout: cleanLayout(value.layout, fallback.layout),
    config: cleanConfig(value.config),
    hidden: value.hidden === true,
  }
}

const enforceLimits = (sections: OverviewSection[], mode: OverviewMode): OverviewSection[] => {
  const counts = new Map<OverviewSectionKind, number>()
  const out: OverviewSection[] = []
  for (const item of sections) {
    if (mode === 'PUBLIC' && TEAM_ONLY_OVERVIEW_SECTIONS.includes(item.kind)) continue
    const nextCount = (counts.get(item.kind) ?? 0) + 1
    if (nextCount > OVERVIEW_SECTION_LIMITS[item.kind]) continue
    counts.set(item.kind, nextCount)
    out.push(item)
  }
  return out
}

const sanitizeModeState = (raw: unknown, mode: OverviewMode): OverviewPageModeState => {
  const value = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
  const sections = Array.isArray(value.sections)
    ? value.sections.map((item, index) => sanitizeSection(item, index, mode)).filter((item): item is OverviewSection => Boolean(item))
    : []
  const shelf = Array.isArray(value.shelf)
    ? value.shelf.map((item, index) => sanitizeSection(item, index, mode)).filter((item): item is OverviewSection => Boolean(item))
    : []
  return {
    sections: enforceLimits(sections, mode),
    shelf: enforceLimits(shelf, mode),
  }
}

const sanitizeOverview = (id: string, data: Record<string, unknown> | undefined): OverviewDocument | null => {
  if (!data || !data.modes || typeof data.modes !== 'object') return null
  const modes = data.modes as Record<string, unknown>
  return {
    id,
    modes: {
      TEAM: sanitizeModeState(modes.TEAM, 'TEAM'),
      PUBLIC: sanitizeModeState(modes.PUBLIC, 'PUBLIC'),
    },
    theme: cleanTheme(data.theme),
    createdBy: cleanString(data.createdBy),
    createdByName: cleanString(data.createdByName),
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
  }
}

const debugKey = (workspaceId: string) => `timelining:debug-overview:${workspaceId}`

const readDebugOverview = (workspaceId: string): OverviewDocument | null => {
  try {
    const raw = window.localStorage.getItem(debugKey(workspaceId))
    if (!raw) return null
    return sanitizeOverview(`debug-${workspaceId}`, JSON.parse(raw) as Record<string, unknown>)
  } catch {
    return null
  }
}

const writeDebugOverview = (workspaceId: string, overview: OverviewDocument): void => {
  try {
    window.localStorage.setItem(debugKey(workspaceId), JSON.stringify({
      modes: overview.modes,
      theme: overview.theme ?? null,
      createdBy: overview.createdBy,
      createdByName: overview.createdByName,
      createdAt: null,
    }))
  } catch {
    // Local debug persistence is best-effort only.
  }
}

export const subscribeLatestOverview = (
  workspaceId: string,
  onChange: (overview: OverviewDocument | null) => void,
  actor: { userId?: string; userName?: string } = {},
) => {
  if (isNoSignInPreviewActive()) {
    onChange(readDebugOverview(workspaceId) ?? createDefaultOverviewDocument(workspaceId, actor))
    return () => undefined
  }
  return onSnapshot(
    query(versionsCollection(workspaceId), orderBy('createdAt', 'desc'), limit(1)),
    (snap) => {
      const docSnap = snap.docs[0]
      if (!docSnap) {
        onChange(createDefaultOverviewDocument(workspaceId, actor))
        return
      }
      const overview = sanitizeOverview(docSnap.id, docSnap.data())
      onChange(overview ?? createDefaultOverviewDocument(workspaceId, actor))
    },
    () => onChange(createDefaultOverviewDocument(workspaceId, actor)),
  )
}

export const subscribeEditingLock = (
  workspaceId: string,
  onChange: (lock: OverviewEditingLock | null) => void,
) => {
  if (isNoSignInPreviewActive()) {
    onChange(null)
    return () => undefined
  }
  return onSnapshot(workspaceDoc(workspaceId), (snap) => {
    const raw = snap.data()?.overviewEditingLock as
      | { userId?: unknown; userName?: unknown; acquiredAt?: unknown }
      | undefined
    if (!raw || typeof raw.userId !== 'string' || typeof raw.userName !== 'string') {
      onChange(null)
      return
    }
    const acquiredAt = raw.acquiredAt instanceof Timestamp ? raw.acquiredAt : null
    if (acquiredAt && Date.now() - acquiredAt.toMillis() > OVERVIEW_LOCK_TTL_MS) {
      onChange(null)
      return
    }
    onChange({ userId: raw.userId, userName: raw.userName, acquiredAt })
  }, () => onChange(null))
}

export const acquireEditingLock = async (
  workspaceId: string,
  userId: string,
  userName: string,
): Promise<{ ok: true } | { ok: false; heldBy: OverviewEditingLock }> => {
  if (isNoSignInPreviewActive()) return { ok: true }
  return await runTransaction(firestoreDb, async (tx) => {
    const snap = await tx.get(workspaceDoc(workspaceId))
    const raw = snap.data()?.overviewEditingLock as
      | { userId?: unknown; userName?: unknown; acquiredAt?: unknown }
      | undefined
    if (raw && typeof raw.userId === 'string' && typeof raw.userName === 'string' && raw.userId !== userId) {
      const acquiredAt = raw.acquiredAt instanceof Timestamp ? raw.acquiredAt : null
      const isLive = acquiredAt && Date.now() - acquiredAt.toMillis() <= OVERVIEW_LOCK_TTL_MS
      if (isLive) return { ok: false as const, heldBy: { userId: raw.userId, userName: raw.userName, acquiredAt } }
    }
    tx.set(workspaceDoc(workspaceId), { overviewEditingLock: { userId, userName, acquiredAt: serverTimestamp() } }, { merge: true })
    return { ok: true as const }
  })
}

export const heartbeatEditingLock = async (
  workspaceId: string,
  userId: string,
  userName: string,
): Promise<void> => {
  if (isNoSignInPreviewActive()) return
  await runTransaction(firestoreDb, async (tx) => {
    const snap = await tx.get(workspaceDoc(workspaceId))
    const raw = snap.data()?.overviewEditingLock as { userId?: unknown } | undefined
    if (!raw || raw.userId !== userId) return
    tx.set(workspaceDoc(workspaceId), { overviewEditingLock: { userId, userName, acquiredAt: serverTimestamp() } }, { merge: true })
  })
}

export const releaseEditingLock = async (workspaceId: string, userId: string): Promise<void> => {
  if (isNoSignInPreviewActive()) return
  await runTransaction(firestoreDb, async (tx) => {
    const snap = await tx.get(workspaceDoc(workspaceId))
    const raw = snap.data()?.overviewEditingLock as { userId?: unknown } | undefined
    if (!raw || raw.userId !== userId) return
    tx.set(workspaceDoc(workspaceId), { overviewEditingLock: null }, { merge: true })
  })
}

const pruneOldVersions = async (workspaceId: string): Promise<void> => {
  const snap = await getDocs(query(versionsCollection(workspaceId), orderBy('createdAt', 'desc')))
  if (snap.size <= OVERVIEW_VERSION_RETENTION) return
  const toDelete = snap.docs.slice(OVERVIEW_VERSION_RETENTION)
  const batch = writeBatch(firestoreDb)
  for (const docToDelete of toDelete) batch.delete(docToDelete.ref)
  await batch.commit()
}

export const saveOverview = async (
  workspaceId: string,
  overview: OverviewDocument,
  actor: { userId: string; userName: string },
): Promise<OverviewDocument> => {
  const sanitized: OverviewDocument = {
    ...overview,
    modes: {
      TEAM: sanitizeModeState(overview.modes.TEAM, 'TEAM'),
      PUBLIC: sanitizeModeState(overview.modes.PUBLIC, 'PUBLIC'),
    },
    theme: cleanTheme(overview.theme),
    createdBy: actor.userId,
    createdByName: actor.userName,
    createdAt: null,
  }
  if (isNoSignInPreviewActive()) {
    writeDebugOverview(workspaceId, sanitized)
    return sanitized
  }
  const newDocRef = doc(versionsCollection(workspaceId))
  const payload: Record<string, unknown> = {
    blocks: [],
    modes: sanitized.modes,
    createdBy: actor.userId,
    createdByName: actor.userName,
    createdAt: serverTimestamp(),
  }
  if (sanitized.theme) payload.theme = sanitized.theme
  await runTransaction(firestoreDb, async (tx) => {
    tx.set(newDocRef, payload)
    tx.set(workspaceDoc(workspaceId), { overviewCurrentVersionId: newDocRef.id, overviewEditingLock: null }, { merge: true })
  })
  void pruneOldVersions(workspaceId).catch(() => undefined)
  const saved = await getDoc(newDocRef)
  return {
    ...sanitized,
    id: newDocRef.id,
    createdAt: saved.data()?.createdAt instanceof Timestamp ? saved.data()?.createdAt as Timestamp : null,
  }
}

// ============================================================================
// Overview v2 Service (R29 O1.a)
// ============================================================================

export type OverviewWorkspaceInput =
  | string
  | {
      id?: string
      description?: string | null
      previewCover?: { tagline?: string | null } | null
    }
  | null
  | undefined

const VALID_BLOCK_WIDTHS: readonly OverviewBlockWidth[] = ['full', 'half', 'third']
const VALID_CALLOUT_TONES: readonly OverviewCalloutTone[] = ['note', 'warning', 'win']
const VALID_IMAGE_RATIOS: readonly OverviewImageRatio[] = ['16:9', '4:3', '1:1', '21:6']
const VALID_EMBED_RATIOS: readonly OverviewEmbedRatio[] = ['16:9', '4:3']
const VALID_CHART_HEIGHTS: readonly OverviewChartHeight[] = ['S', 'M', 'L']

export const createDefaultOverviewV2 = (
  workspace?: OverviewWorkspaceInput,
  actor: { userId?: string; userName?: string } = {},
): OverviewDocumentV2 => {
  const wsId =
    typeof workspace === 'string'
      ? workspace
      : typeof workspace?.id === 'string'
        ? workspace.id
        : ''
  const rawBrief =
    typeof workspace === 'object' && workspace !== null
      ? (typeof workspace.description === 'string'
          ? workspace.description
          : typeof workspace.previewCover?.tagline === 'string'
            ? workspace.previewCover.tagline
            : '')
      : ''

  return {
    id: wsId ? `default-${wsId}` : 'default',
    schemaVersion: 2,
    brief: rawBrief.trim(),
    sections: OVERVIEW_BUILTIN_KEYS.map((key) => ({ kind: 'BUILTIN' as const, key })),
    customSections: [],
    createdBy: actor.userId?.trim() ?? '',
    createdByName: actor.userName?.trim() || 'Timelining',
    createdAt: null,
  }
}

const requireHttpsUrlString = (raw: unknown, fieldLabel: string): string => {
  if (typeof raw !== 'string') {
    throw new Error(`Invalid ${fieldLabel}: expected an https:// URL string.`)
  }
  const trimmed = raw.trim()
  const parsed = isHttpsUrl(trimmed)
  if (!parsed) {
    throw new Error(`Invalid ${fieldLabel}: only https:// URLs are allowed.`)
  }
  return trimmed
}

export const validateBlockV2 = (raw: unknown, sectionIndex: number, blockIndex: number): OverviewBlock => {
  if (!raw || typeof raw !== 'object') {
    throw new Error(`Invalid block at section ${sectionIndex}, index ${blockIndex}.`)
  }
  const b = raw as Record<string, unknown>
  const id = typeof b.id === 'string' ? b.id.trim() : ''
  if (!id) {
    throw new Error(`Block at section ${sectionIndex}, index ${blockIndex} requires a non-empty id.`)
  }
  const width = b.width as OverviewBlockWidth
  if (!VALID_BLOCK_WIDTHS.includes(width)) {
    throw new Error(`Invalid block width "${String(b.width)}" at block "${id}".`)
  }

  switch (b.type) {
    case 'TEXT': {
      if (typeof b.markdown !== 'string') {
        throw new Error(`TEXT block "${id}" requires a markdown string.`)
      }
      const markdown = b.markdown.trim()
      if (markdown.length > OVERVIEW_V2_LIMITS.MAX_TEXT_CHARS) {
        throw new Error(
          `TEXT block "${id}" exceeds limit of ${OVERVIEW_V2_LIMITS.MAX_TEXT_CHARS} characters (${markdown.length}).`,
        )
      }
      return { id, width, type: 'TEXT', markdown }
    }
    case 'CALLOUT': {
      const tone = b.tone as OverviewCalloutTone
      if (!VALID_CALLOUT_TONES.includes(tone)) {
        throw new Error(`CALLOUT block "${id}" has invalid tone "${String(b.tone)}".`)
      }
      if (typeof b.body !== 'string') {
        throw new Error(`CALLOUT block "${id}" requires a body string.`)
      }
      const body = b.body.trim()
      if (body.length > OVERVIEW_V2_LIMITS.MAX_CALLOUT_CHARS) {
        throw new Error(
          `CALLOUT block "${id}" exceeds limit of ${OVERVIEW_V2_LIMITS.MAX_CALLOUT_CHARS} characters (${body.length}).`,
        )
      }
      const title = typeof b.title === 'string' ? b.title.trim() : undefined
      return {
        id,
        width,
        type: 'CALLOUT',
        tone,
        ...(title ? { title } : {}),
        body,
      }
    }
    case 'IMAGE': {
      const ratio = b.ratio as OverviewImageRatio
      if (!VALID_IMAGE_RATIOS.includes(ratio)) {
        throw new Error(`IMAGE block "${id}" has invalid ratio "${String(b.ratio)}".`)
      }
      const alt = typeof b.alt === 'string' ? b.alt.trim() : ''
      const sourceRaw = b.source && typeof b.source === 'object' ? (b.source as Record<string, unknown>) : null
      if (!sourceRaw) {
        throw new Error(`IMAGE block "${id}" requires a source.`)
      }
      if ('uploadId' in sourceRaw && typeof sourceRaw.uploadId === 'string') {
        const uploadId = sourceRaw.uploadId.trim()
        if (!uploadId) {
          throw new Error(`IMAGE block "${id}" uploadId cannot be empty.`)
        }
        return { id, width, type: 'IMAGE', source: { uploadId }, alt, ratio }
      }
      if ('url' in sourceRaw) {
        const url = requireHttpsUrlString(sourceRaw.url, `IMAGE block "${id}" url`)
        return { id, width, type: 'IMAGE', source: { url }, alt, ratio }
      }
      throw new Error(`IMAGE block "${id}" source must contain uploadId or https url.`)
    }
    case 'EMBED': {
      const ratio = b.ratio as OverviewEmbedRatio
      if (!VALID_EMBED_RATIOS.includes(ratio)) {
        throw new Error(`EMBED block "${id}" has invalid ratio "${String(b.ratio)}".`)
      }
      const urlStr = requireHttpsUrlString(b.url, `EMBED block "${id}" url`)
      const parsed = isHttpsUrl(urlStr)!
      if (!isEmbedAllowed(parsed)) {
        throw new Error(`EMBED block "${id}" host "${parsed.hostname}" is not in the embed allowlist.`)
      }
      return { id, width, type: 'EMBED', url: urlStr, ratio }
    }
    case 'LINKS': {
      if (!Array.isArray(b.items)) {
        throw new Error(`LINKS block "${id}" requires an items array.`)
      }
      const items: Array<{ ref: WorkspaceItemReference } | { url: string; label: string }> = b.items.map(
        (entry, itemIdx) => {
          if (!entry || typeof entry !== 'object') {
            throw new Error(`LINKS block "${id}" item ${itemIdx} is invalid.`)
          }
          const rec = entry as Record<string, unknown>
          if ('ref' in rec && rec.ref && typeof rec.ref === 'object') {
            const refObj = rec.ref as Record<string, unknown>
            const refId = typeof refObj.id === 'string' ? refObj.id.trim() : ''
            const refLabel = typeof refObj.label === 'string' ? refObj.label.trim() : ''
            const refKind = typeof refObj.kind === 'string' ? (refObj.kind.trim() as WorkspaceItemReference['kind']) : null
            if (!refId || !refKind) {
              throw new Error(`LINKS block "${id}" item ${itemIdx} ref requires id and kind.`)
            }
            return {
              ref: {
                id: refId,
                label: refLabel,
                kind: refKind,
                parentId: typeof refObj.parentId === 'string' ? refObj.parentId.trim() : null,
                ancestors: Array.isArray(refObj.ancestors) ? (refObj.ancestors as WorkspaceItemReference['ancestors']) : [],
              },
            }
          }
          const url = requireHttpsUrlString(rec.url, `LINKS block "${id}" item ${itemIdx} url`)
          const label = typeof rec.label === 'string' ? rec.label.trim() : ''
          if (!label) {
            throw new Error(`LINKS block "${id}" item ${itemIdx} requires a non-empty label.`)
          }
          return { url, label }
        },
      )
      return { id, width, type: 'LINKS', items }
    }
    case 'PEOPLE': {
      if (!Array.isArray(b.items)) {
        throw new Error(`PEOPLE block "${id}" requires an items array.`)
      }
      const items = b.items.map((entry, itemIdx) => {
        if (!entry || typeof entry !== 'object') {
          throw new Error(`PEOPLE block "${id}" item ${itemIdx} is invalid.`)
        }
        const rec = entry as Record<string, unknown>
        const uid = typeof rec.uid === 'string' ? rec.uid.trim() : ''
        if (!uid) {
          throw new Error(`PEOPLE block "${id}" item ${itemIdx} requires a non-empty uid.`)
        }
        const note = typeof rec.note === 'string' ? rec.note.trim() : undefined
        return { uid, ...(note ? { note } : {}) }
      })
      return { id, width, type: 'PEOPLE', items }
    }
    case 'CHART': {
      const chartId = typeof b.chartId === 'string' ? b.chartId.trim() : ''
      if (!chartId) {
        throw new Error(`CHART block "${id}" requires a non-empty chartId.`)
      }
      const height = b.height as OverviewChartHeight
      if (!VALID_CHART_HEIGHTS.includes(height)) {
        throw new Error(`CHART block "${id}" has invalid height "${String(b.height)}".`)
      }
      return { id, width, type: 'CHART', chartId, height }
    }
    default:
      throw new Error(`Unknown block type "${String(b.type)}" at block "${id}".`)
  }
}

export const validateAndSanitizeOverviewV2 = (
  docInput: OverviewDocumentV2,
  actor: { userId: string; userName: string },
): OverviewDocumentV2 => {
  if (!docInput || typeof docInput !== 'object' || docInput.schemaVersion !== 2) {
    throw new Error('Overview document must have schemaVersion: 2.')
  }
  const customSectionsInput = Array.isArray(docInput.customSections) ? docInput.customSections : []
  if (customSectionsInput.length > OVERVIEW_V2_LIMITS.MAX_CUSTOM_SECTIONS) {
    throw new Error(
      `Overview exceeds maximum of ${OVERVIEW_V2_LIMITS.MAX_CUSTOM_SECTIONS} custom sections (${customSectionsInput.length}).`,
    )
  }

  const customSections: OverviewCustomSection[] = customSectionsInput.map((sec, sIdx) => {
    if (!sec || typeof sec !== 'object') {
      throw new Error(`Invalid custom section at index ${sIdx}.`)
    }
    const id = typeof sec.id === 'string' ? sec.id.trim() : ''
    if (!id) {
      throw new Error(`Custom section at index ${sIdx} requires a non-empty id.`)
    }
    const title = typeof sec.title === 'string' ? sec.title.trim() : ''
    if (!title) {
      throw new Error(`Custom section "${id}" requires a non-empty title.`)
    }
    const blocksInput = Array.isArray(sec.blocks) ? sec.blocks : []
    if (blocksInput.length > OVERVIEW_V2_LIMITS.MAX_BLOCKS_PER_SECTION) {
      throw new Error(
        `Custom section "${id}" exceeds maximum of ${OVERVIEW_V2_LIMITS.MAX_BLOCKS_PER_SECTION} blocks (${blocksInput.length}).`,
      )
    }
    const blocks = blocksInput.map((blk, bIdx) => validateBlockV2(blk, sIdx, bIdx))
    return { id, title, blocks }
  })

  const customIds = new Set(customSections.map((s) => s.id))
  const seenBuiltins = new Set<OverviewBuiltinKey>()
  const sectionsInput = Array.isArray(docInput.sections) ? docInput.sections : []
  const sections: OverviewSectionRef[] = []

  for (const ref of sectionsInput) {
    if (!ref || typeof ref !== 'object') continue
    if (ref.kind === 'BUILTIN') {
      const key = ref.key as OverviewBuiltinKey
      if (OVERVIEW_BUILTIN_KEYS.includes(key) && !seenBuiltins.has(key)) {
        seenBuiltins.add(key)
        sections.push({
          kind: 'BUILTIN',
          key,
          ...(ref.hidden === true ? { hidden: true } : {}),
        })
      }
    } else if (ref.kind === 'CUSTOM') {
      const id = typeof ref.id === 'string' ? ref.id.trim() : ''
      if (id && customIds.has(id)) {
        sections.push({
          kind: 'CUSTOM',
          id,
          ...(ref.hidden === true ? { hidden: true } : {}),
        })
      }
    }
  }

  // Ensure all 7 built-in sections are present in order if omitted
  for (const key of OVERVIEW_BUILTIN_KEYS) {
    if (!seenBuiltins.has(key)) {
      seenBuiltins.add(key)
      sections.push({ kind: 'BUILTIN', key })
    }
  }

  const brief = typeof docInput.brief === 'string' ? docInput.brief.trim() : ''

  return {
    id: typeof docInput.id === 'string' && docInput.id.trim() ? docInput.id.trim() : 'default',
    schemaVersion: 2,
    brief,
    sections,
    customSections,
    createdBy: actor.userId.trim(),
    createdByName: actor.userName.trim() || 'Timelining',
    createdAt: docInput.createdAt instanceof Timestamp ? docInput.createdAt : null,
  }
}

export const parseOverviewSnapshotV2 = (
  id: string,
  data: Record<string, unknown> | undefined,
  workspace?: OverviewWorkspaceInput,
  actor: { userId?: string; userName?: string } = {},
): OverviewDocumentV2 => {
  // OV-D8: A v1 doc (no schemaVersion or schemaVersion !== 2) is ignored and treated as the default.
  if (!data || data.schemaVersion !== 2) {
    return createDefaultOverviewV2(workspace, actor)
  }
  try {
    const candidate: OverviewDocumentV2 = {
      id,
      schemaVersion: 2,
      brief: typeof data.brief === 'string' ? data.brief : '',
      sections: Array.isArray(data.sections) ? (data.sections as OverviewSectionRef[]) : [],
      customSections: Array.isArray(data.customSections)
        ? (data.customSections as OverviewCustomSection[])
        : [],
      createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
      createdByName: typeof data.createdByName === 'string' ? data.createdByName : 'Timelining',
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
    }
    const validated = validateAndSanitizeOverviewV2(candidate, {
      userId: candidate.createdBy || actor.userId || '',
      userName: candidate.createdByName || actor.userName || 'Timelining',
    })
    return {
      ...validated,
      id,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt : null,
    }
  } catch {
    return createDefaultOverviewV2(workspace, actor)
  }
}

const debugKeyV2 = (workspaceId: string) => `timelining:debug-overview-v2:${workspaceId}`

const readDebugOverviewV2 = (
  workspaceId: string,
  workspace?: OverviewWorkspaceInput,
  actor: { userId?: string; userName?: string } = {},
): OverviewDocumentV2 | null => {
  try {
    const raw = window.localStorage.getItem(debugKeyV2(workspaceId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || parsed.schemaVersion !== 2) return null
    return parseOverviewSnapshotV2(`debug-${workspaceId}`, parsed, workspace, actor)
  } catch {
    return null
  }
}

const writeDebugOverviewV2 = (workspaceId: string, overview: OverviewDocumentV2): void => {
  try {
    window.localStorage.setItem(
      debugKeyV2(workspaceId),
      JSON.stringify({
        ...overview,
        blocks: [],
        createdAt: null,
      }),
    )
  } catch {
    // Local debug persistence is best-effort only.
  }
}

export const subscribeOverviewV2 = (
  workspaceId: string,
  cb: (overview: OverviewDocumentV2) => void,
  options: {
    workspace?: OverviewWorkspaceInput
    actor?: { userId?: string; userName?: string }
  } = {},
) => {
  const wsInput = options.workspace ?? workspaceId
  const actor = options.actor ?? {}
  if (isNoSignInPreviewActive()) {
    cb(readDebugOverviewV2(workspaceId, wsInput, actor) ?? createDefaultOverviewV2(wsInput, actor))
    return () => undefined
  }
  return onSnapshot(
    query(versionsCollection(workspaceId), orderBy('createdAt', 'desc'), limit(1)),
    (snap) => {
      const docSnap = snap.docs[0]
      if (!docSnap) {
        cb(createDefaultOverviewV2(wsInput, actor))
        return
      }
      cb(parseOverviewSnapshotV2(docSnap.id, docSnap.data(), wsInput, actor))
    },
    () => cb(createDefaultOverviewV2(wsInput, actor)),
  )
}

export const saveOverviewV2 = async (
  workspaceId: string,
  docInput: OverviewDocumentV2,
  actor: { userId: string; userName: string },
): Promise<OverviewDocumentV2> => {
  const sanitized = validateAndSanitizeOverviewV2(docInput, actor)
  if (isNoSignInPreviewActive()) {
    writeDebugOverviewV2(workspaceId, sanitized)
    return sanitized
  }
  const newDocRef = doc(versionsCollection(workspaceId))
  const payload: Record<string, unknown> = {
    ...sanitized,
    id: newDocRef.id,
    blocks: [],
    createdAt: serverTimestamp(),
  }
  await runTransaction(firestoreDb, async (tx) => {
    tx.set(newDocRef, payload)
    tx.set(
      workspaceDoc(workspaceId),
      { overviewCurrentVersionId: newDocRef.id, overviewEditingLock: null },
      { merge: true },
    )
  })
  void pruneOldVersions(workspaceId).catch(() => undefined)
  const saved = await getDoc(newDocRef)
  return {
    ...sanitized,
    id: newDocRef.id,
    createdAt: saved.data()?.createdAt instanceof Timestamp ? (saved.data()?.createdAt as Timestamp) : null,
  }
}

// ---------------------------------------------------------------------------
// Overview Summary Callable
// ---------------------------------------------------------------------------

export type OverviewSummaryTier = 'lead' | 'viewer' | 'helper'

export interface OverviewSummaryFeedItem {
  actorId: string
  actorName: string
  summary: string
  targetTitle: string
  createdAt: string | null
}

export interface OverviewSummaryPersonEntry {
  lastActiveAt: string | null
  series7d: number[]
}

export interface OverviewSummaryResult {
  tier: OverviewSummaryTier
  activeCount7d: number
  people: Record<string, OverviewSummaryPersonEntry>
  feed: OverviewSummaryFeedItem[] | null
  generatedAt: string
}

export const getOverviewSummary = async (workspaceId: string): Promise<OverviewSummaryResult> => {
  if (isNoSignInPreviewActive()) {
    return {
      tier: 'lead',
      activeCount7d: 0,
      people: {},
      feed: [],
      generatedAt: new Date().toISOString(),
    }
  }
  const { callable } = await import('./callable')
  return callable<{ workspaceId: string }, OverviewSummaryResult>('getOverviewSummary', { workspaceId })
}

